import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, FileSpreadsheet, FileText, Calendar, Building2 } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '@/lib/api';
const API = `${BACKEND_URL}/api`;

const BankLedger = ({ user }) => {
  const { bankAccount } = useParams();
  const navigate = useNavigate();
  const decodedBankAccount = decodeURIComponent(bankAccount || '');
  
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('this_month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [openingBalance, setOpeningBalance] = useState(0);

  useEffect(() => {
    if (decodedBankAccount) {
      fetchLedger();
    }
  }, [decodedBankAccount, dateFilter, customStartDate, customEndDate]);

  const getDateRange = () => {
    const today = new Date();
    let startDate, endDate;

    // Helper to format date as YYYY-MM-DD in local timezone (not UTC)
    const formatLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    switch (dateFilter) {
      case 'this_week':
        const dayOfWeek = today.getDay();
        startDate = new Date(today);
        startDate.setDate(today.getDate() - dayOfWeek);
        endDate = today;
        break;
      case 'this_month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = today;
        break;
      case 'last_month':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'this_year':
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = today;
        break;
      case 'custom':
        startDate = customStartDate ? new Date(customStartDate) : new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = customEndDate ? new Date(customEndDate) : today;
        break;
      default:
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = today;
    }

    return {
      start: formatLocalDate(startDate),
      end: formatLocalDate(endDate)
    };
  };

  const fetchLedger = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const { start, end } = getDateRange();

      // Fetch all transactions for this bank account
      const response = await axios.get(`${API}/bank-transactions`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { bank_account: decodedBankAccount }
      });

      const allTransactions = response.data;

      // Calculate opening balance (sum of all transactions before start date)
      const beforeStartDate = allTransactions.filter(t => t.date < start);
      const openingBal = beforeStartDate.reduce((bal, t) => {
        if (['credit', 'transfer_in'].includes(t.transaction_type)) {
          return bal + t.amount;
        } else {
          return bal - t.amount;
        }
      }, 0);
      setOpeningBalance(openingBal);

      // Filter transactions within date range
      const filtered = allTransactions.filter(t => t.date >= start && t.date <= end);

      setTransactions(filtered);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch ledger');
      setLoading(false);
    }
  };

  // Calculate running balance for each transaction (ascending order for balance calculation)
  const getTransactionsWithBalance = () => {
    // Sort ascending first to calculate running balance correctly
    const sortedAsc = [...transactions].sort((a, b) => {
      const dateCompare = new Date(a.date) - new Date(b.date);
      if (dateCompare !== 0) return dateCompare;
      return new Date(a.created_at) - new Date(b.created_at);
    });

    let runningBalance = openingBalance;
    const withBalance = sortedAsc.map(t => {
      const moneyIn = ['credit', 'transfer_in'].includes(t.transaction_type) ? t.amount : 0;
      const moneyOut = ['debit', 'transfer_out', 'cash_withdrawal'].includes(t.transaction_type) ? t.amount : 0;
      runningBalance += moneyIn - moneyOut;
      return {
        ...t,
        moneyIn,
        moneyOut,
        balance: runningBalance
      };
    });

    // Return in descending order (latest on top) for display
    return withBalance.reverse();
  };

  const transactionsWithBalance = getTransactionsWithBalance();
  
  // Closing balance is the balance after all transactions (the first item in reversed array)
  const closingBalance = transactionsWithBalance.length > 0 
    ? transactionsWithBalance[0].balance 
    : openingBalance;

  const totalMoneyIn = transactionsWithBalance.reduce((sum, t) => sum + t.moneyIn, 0);
  const totalMoneyOut = transactionsWithBalance.reduce((sum, t) => sum + t.moneyOut, 0);

  const exportToExcel = () => {
    const { start, end } = getDateRange();
    
    // For Excel, show in chronological order (oldest first)
    const chronologicalOrder = [...transactionsWithBalance].reverse();
    
    // Create CSV content
    let csv = `Bank Ledger - ${decodedBankAccount}\n`;
    csv += `Period: ${start} to ${end}\n\n`;
    csv += `Opening Balance,,,,"₹${openingBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}"\n\n`;
    csv += `Date,Transaction Detail,Money In,Money Out,Balance\n`;
    
    chronologicalOrder.forEach(t => {
      const date = new Date(t.date).toLocaleDateString('en-IN');
      const detail = `"${t.reference || t.transaction_type}"`;
      const moneyIn = t.moneyIn > 0 ? t.moneyIn.toFixed(2) : '';
      const moneyOut = t.moneyOut > 0 ? t.moneyOut.toFixed(2) : '';
      const balance = t.balance.toFixed(2);
      csv += `${date},${detail},${moneyIn},${moneyOut},${balance}\n`;
    });

    csv += `\nTotal,,${totalMoneyIn.toFixed(2)},${totalMoneyOut.toFixed(2)},\n`;
    csv += `Closing Balance,,,,"₹${closingBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}"\n`;

    // Download using file-saver library
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `${decodedBankAccount}_Ledger_${start}_to_${end}.csv`);
    toast.success('Excel file downloaded!');
  };

  const exportToPDF = () => {
    const { start, end } = getDateRange();
    
    // For PDF, show in chronological order (oldest first)
    const chronologicalOrder = [...transactionsWithBalance].reverse();
    
    // Create printable HTML - Portrait mode
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Bank Ledger - ${decodedBankAccount}</title>
        <style>
          @page { size: portrait; margin: 15mm; }
          body { font-family: Arial, sans-serif; padding: 10px; font-size: 11px; }
          h1 { text-align: center; color: #1e40af; font-size: 18px; margin-bottom: 5px; }
          h2 { text-align: center; font-size: 14px; color: #333; margin-top: 0; }
          .header { text-align: center; margin-bottom: 15px; }
          .period { color: #666; margin-bottom: 8px; font-size: 12px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: right; font-size: 10px; }
          th { background-color: #1e40af; color: white; font-size: 10px; }
          td:first-child, th:first-child { text-align: left; width: 80px; }
          td:nth-child(2), th:nth-child(2) { text-align: left; max-width: 180px; word-wrap: break-word; }
          .money-in { color: #16a34a; }
          .money-out { color: #dc2626; }
          .balance { font-weight: bold; }
          .opening-row, .closing-row { background-color: #f0f9ff; font-weight: bold; }
          .total-row { background-color: #fef3c7; font-weight: bold; }
          .footer { margin-top: 20px; text-align: center; color: #666; font-size: 9px; }
          .summary { display: flex; justify-content: space-between; margin-top: 15px; padding: 10px; background: #f8f9fa; border-radius: 4px; }
          .summary-item { text-align: center; }
          .summary-label { font-size: 9px; color: #666; }
          .summary-value { font-size: 12px; font-weight: bold; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>HOPPERS FINANCE DSR</h1>
          <h2>Bank Ledger - ${decodedBankAccount}</h2>
          <p class="period">Period: ${new Date(start).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} to ${new Date(end).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
        </div>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Transaction Detail</th>
              <th>Money In (₹)</th>
              <th>Money Out (₹)</th>
              <th>Balance (₹)</th>
            </tr>
          </thead>
          <tbody>
            <tr class="opening-row">
              <td colspan="4">Opening Balance</td>
              <td class="balance">${openingBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr>
            ${chronologicalOrder.map(t => `
              <tr>
                <td>${new Date(t.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                <td>${t.reference || t.transaction_type}</td>
                <td class="money-in">${t.moneyIn > 0 ? t.moneyIn.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '-'}</td>
                <td class="money-out">${t.moneyOut > 0 ? t.moneyOut.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '-'}</td>
                <td class="balance">${t.balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
              </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="2">Total</td>
              <td class="money-in">${totalMoneyIn.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
              <td class="money-out">${totalMoneyOut.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
              <td></td>
            </tr>
            <tr class="closing-row">
              <td colspan="4">Closing Balance</td>
              <td class="balance">${closingBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr>
          </tbody>
        </table>
        <div class="footer">
          <p>Generated on ${new Date().toLocaleString('en-IN')}</p>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
    toast.success('PDF ready for print!');
  };

  const goBack = () => {
    navigate('/banks');
  };

  return (
    <div className="space-y-6" data-testid="bank-ledger-page">
      {/* Header with back button */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={goBack} data-testid="back-to-banks-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Bank Management
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Building2 className="w-5 h-5 text-blue-600" />
            Bank Ledger - {decodedBankAccount}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 p-4 bg-gray-50 rounded-lg items-center">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium">Period:</span>
            </div>
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-40" data-testid="date-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="this_week">This Week</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="last_month">Last Month</SelectItem>
                <SelectItem value="this_year">This Year</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>

            {dateFilter === 'custom' && (
              <>
                <Input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-40"
                  data-testid="custom-start-date"
                />
                <span className="text-gray-500">to</span>
                <Input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-40"
                  data-testid="custom-end-date"
                />
              </>
            )}

            <div className="flex-1" />

            {/* Export Buttons */}
            <Button variant="outline" size="sm" onClick={exportToExcel} data-testid="export-excel-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
              Excel
            </Button>
            <Button variant="outline" size="sm" onClick={exportToPDF} data-testid="export-pdf-btn">
              <FileText className="w-4 h-4 mr-2 text-red-600" />
              PDF
            </Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gray-50">
              <CardContent className="pt-4">
                <p className="text-xs text-gray-500">Opening Balance</p>
                <p className="text-lg font-bold text-gray-700" data-testid="opening-balance">
                  ₹{openingBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-green-50">
              <CardContent className="pt-4">
                <p className="text-xs text-green-600">Total Money In</p>
                <p className="text-lg font-bold text-green-700" data-testid="total-money-in">
                  ₹{totalMoneyIn.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-red-50">
              <CardContent className="pt-4">
                <p className="text-xs text-red-600">Total Money Out</p>
                <p className="text-lg font-bold text-red-700" data-testid="total-money-out">
                  ₹{totalMoneyOut.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50">
              <CardContent className="pt-4">
                <p className="text-xs text-blue-600">Closing Balance</p>
                <p className="text-lg font-bold text-blue-700" data-testid="closing-balance">
                  ₹{closingBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Ledger Table */}
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-blue-900 hover:bg-blue-900">
                    <TableHead className="text-white font-semibold">Date</TableHead>
                    <TableHead className="text-white font-semibold">Transaction Detail</TableHead>
                    <TableHead className="text-white font-semibold text-right">Money In (₹)</TableHead>
                    <TableHead className="text-white font-semibold text-right">Money Out (₹)</TableHead>
                    <TableHead className="text-white font-semibold text-right">Balance (₹)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactionsWithBalance.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                        No transactions in this period
                      </TableCell>
                    </TableRow>
                  ) : (
                    transactionsWithBalance.map((t, index) => (
                      <TableRow key={t.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`ledger-row-${t.id}`}>
                        <TableCell>
                          {new Date(t.date).toLocaleDateString('en-IN', { 
                            day: '2-digit', 
                            month: 'short', 
                            year: 'numeric' 
                          })}
                        </TableCell>
                        <TableCell className="max-w-xs">
                          <div className="truncate" title={t.reference || t.transaction_type}>
                            {t.reference || t.transaction_type}
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-green-600 font-medium">
                          {t.moneyIn > 0 ? t.moneyIn.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '-'}
                        </TableCell>
                        <TableCell className="text-right text-red-600 font-medium">
                          {t.moneyOut > 0 ? t.moneyOut.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '-'}
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          {t.balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </TableCell>
                      </TableRow>
                    ))
                  )}

                  {/* Summary Rows */}
                  {transactionsWithBalance.length > 0 && (
                    <>
                      {/* Total Row */}
                      <TableRow className="bg-yellow-50 font-bold">
                        <TableCell colSpan={2}>Total</TableCell>
                        <TableCell className="text-right text-green-700">
                          {totalMoneyIn.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right text-red-700">
                          {totalMoneyOut.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>

                      {/* Opening Balance Row */}
                      <TableRow className="bg-gray-100 font-medium">
                        <TableCell colSpan={4}>Opening Balance (Before Period)</TableCell>
                        <TableCell className="text-right font-bold">
                          {openingBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </TableCell>
                      </TableRow>

                      {/* Closing Balance Row */}
                      <TableRow className="bg-blue-100 font-bold">
                        <TableCell colSpan={4}>Closing Balance</TableCell>
                        <TableCell className="text-right text-blue-800 text-lg">
                          {closingBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BankLedger;
